import { ref, provide } from 'vue'

/**
 * Modal Management with Provide/Inject Pattern
 * 
 * This implementation uses provide/inject pattern to manage modals across components
 * without prop drilling or relying on Vuex for state management.
 * 
 * === HOW TO USE THIS PATTERN ===
 * 
 * 1. SINGLE MODAL:
 *    - Import the useModalProvider composable:
 *      import { useModalProvider } from '@/composables/useModalProvider';
 *    - Use it directly in component:
 *      const { openModal, closeModal, isModalOpen } = useModalProvider();
 *    - The modal component doesn't need any special configuration
 * 
 * 2. MULTIPLE MODALS:
 *    - Create multiple providers with unique keys:
 *      const { openModal: openFirstModal } = useModalProvider('firstModal');
 *      const { openModal: openSecondModal } = useModalProvider('secondModal');
 *    - Specify the key in each modal component:
 *      <PFBaseModal modalControlKey="firstModal" />
 *      <PFBaseModal modalControlKey="secondModal" />
 */
export function useModalProvider(key = 'modalControl') {
  const isModalOpen = ref(false)
  
  const openModal = () => {
    isModalOpen.value = true
  }
  
  const closeModal = () => {
    isModalOpen.value = false
  }
  
  provide(key, {
    isModalOpen,
    openModal,
    closeModal
  })
  
  return {
    isModalOpen,
    openModal,
    closeModal
  }
}